import { useSyncExternalStore } from 'react';

/**
 * Delat läge för bottenmenyn (BottomNav): om en karta är i helskärm (då göms menyn) och vilken
 * jaktmark/flik som är öppen (Karta och Chatt visas som valda). KartaTab och MyMarkDetailView
 * rapporterar hit; menyn läser med useNavState.
 */
export interface NavState {
  /** Minst en karta i helskärm. */
  mapFullscreen: boolean;
  /** Den jaktmark som är öppen i Min jakt och dess flik, annars null. */
  openMark: { id: string; tab: string } | null;
}

let fullscreenHolders = 0;
let state: NavState = { mapFullscreen: false, openMark: null };
const listeners = new Set<() => void>();

function set(next: Partial<NavState>) {
  state = { ...state, ...next };
  listeners.forEach(l => l());
}

/** En karta går i helskärm. Returnerar en funktion som släpper den (flera kartor kan hålla samtidigt). */
export function holdMapFullscreen(): () => void {
  fullscreenHolders++;
  if (!state.mapFullscreen) set({ mapFullscreen: true });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    fullscreenHolders = Math.max(0, fullscreenHolders - 1);
    if (fullscreenHolders === 0) set({ mapFullscreen: false });
  };
}

const LAST_MARK_KEY = 'qvenari_last_mark';

/** Jaktmarken och fliken som visas just nu; null när marken stängs. Marken sparas som senast öppnad. */
export function setOpenMark(mark: { id: string; tab: string } | null) {
  const cur = state.openMark;
  if (cur?.id === mark?.id && cur?.tab === mark?.tab) return;
  if (mark) { try { localStorage.setItem(LAST_MARK_KEY, mark.id); } catch { /* privat läge */ } }
  set({ openMark: mark });
}

/** Senast öppnade jaktmark (för bottenmenyns Karta och Chatt). */
export function lastMarkId(): string | null {
  try { return localStorage.getItem(LAST_MARK_KEY); } catch { return null; }
}

/** Vilken mark Karta/Chatt ska öppna: den öppna, annars den senaste om den finns kvar, annars den första riktiga. */
export function pickMarkId<M extends { id: string }>(marks: M[], isDemo: (m: M) => boolean, openId?: string | null, lastId?: string | null): string | null {
  if (openId && marks.some(m => m.id === openId)) return openId;
  if (lastId && marks.some(m => m.id === lastId)) return lastId;
  return (marks.find(m => !isDemo(m)) ?? marks[0])?.id ?? null;
}

export function getNavState(): NavState { return state; }

function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }

export function useNavState(): NavState {
  return useSyncExternalStore(subscribe, getNavState, getNavState);
}

/** Hur många punkter som får plats i en rad som är `width` px bred (minst 3, högst `max`). */
export function navSlots(width: number, minItem = 62, max = 6): number {
  return Math.max(3, Math.min(max, Math.floor(width / minItem)));
}

/** Delar upp punkterna: får alla plats visas de, annars de första (platser − 1) och resten under "Mer". */
export function splitNavItems<T>(items: T[], slots: number): { primary: T[]; more: T[] } {
  const room = slots - 1; // en plats går alltid till Mer (Min jakt, Mer jakt, profil …)
  return { primary: items.slice(0, room), more: items.slice(room) };
}

/** UX4: jaktknappen på en marks karta valde en annan mark – den marken startar/planerar jakt när den öppnas. */
let pendingHuntAction: { markId: string; action: 'start' | 'plan' } | null = null;
export function requestHuntAction(markId: string, action: 'start' | 'plan') { pendingHuntAction = { markId, action }; }
/** Hämtar (och tömmer) en väntande jaktåtgärd för marken. */
export function takeHuntAction(markId: string): 'start' | 'plan' | null {
  if (pendingHuntAction?.markId !== markId) return null;
  const a = pendingHuntAction.action;
  pendingHuntAction = null;
  return a;
}
